/**
 * Store Zustand pour gérer l'état d'accès global de l'application
 * Source unique de vérité pour les autorisations
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Bootstrap,
  UserInfo,
  SectionsMap,
  CapabilitiesMap,
  UserScope,
  CountsMap,
} from '@sphere/auth/domain';

interface AccessState {
  // État d'authentification
  authenticated: boolean;
  bootstrapLoaded: boolean;

  // Données utilisateur
  user: UserInfo | null;
  roles: string[];
  groups: string[];

  // Contrôle d'accès
  sections: SectionsMap;
  capabilities: CapabilitiesMap;
  scope: UserScope;
  counts: CountsMap;

  // Flag pour mode dégradé
  partial: boolean;

  // Actions
  setBootstrap: (bootstrap: Bootstrap) => void;
  clear: () => void;

  // Helpers
  isAdmin: () => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  isSectionVisible: (sectionKey: string) => boolean;
  canList: (resourceKey: string) => boolean;
  canCreate: (resourceKey: string) => boolean;
  getCount: (key: string) => number;
}

const initialState = {
  authenticated: false,
  bootstrapLoaded: false,
  user: null,
  roles: [],
  groups: [],
  sections: {},
  capabilities: {},
  scope: {
    establishmentIds: null,
    classIds: null,
  },
  counts: {},
  partial: false,
};

export const useAccessStore = create<AccessState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setBootstrap: (bootstrap: Bootstrap) => {
        console.log('🔍 DEBUG setBootstrap - Received bootstrap:', {
          hasScope: !!bootstrap.scope,
          scope: bootstrap.scope,
          establishmentIds: bootstrap.scope?.establishmentIds,
          classIds: bootstrap.scope?.classIds,
          fullBootstrap: bootstrap,
        });
        set({
          authenticated: true,
          bootstrapLoaded: true,
          user: bootstrap.user,
          roles: bootstrap.user.roles,
          groups: bootstrap.user.groups || [],
          sections: bootstrap.sections,
          capabilities: bootstrap.capabilities,
          scope: bootstrap.scope,
          counts: bootstrap.counts,
          partial: bootstrap.partial || false,
        });
      },

      clear: () => {
        set({
          authenticated: false,
          bootstrapLoaded: false,
          user: null,
          roles: [],
          groups: [],
          sections: {},
          capabilities: {},
          scope: { establishmentIds: null, classIds: null },
          counts: {},
          partial: false,
        });
      },

      isAdmin: () => {
        const { roles, groups } = get();

        // Vérifier tous les noms de rôles admin possibles dans Keycloak
        const adminRoles = [
          'admin',
          'administrateur',
          'realm-admin',
          'Admin',
          'ADMIN',
          'Administrateur',
        ];

        const hasAdminRole = roles.some((role) => adminRoles.includes(role));
        if (hasAdminRole) return true;

        // Vérifier les groupes si fournis
        if (groups && groups.length > 0) {
          const adminGroups = [
            'Admin',
            'Admins',
            'Administrateurs',
            'admin',
            'admins',
            'administrateurs',
          ];
          const hasAdminGroup = groups.some((group) =>
            adminGroups.includes(group)
          );
          return hasAdminGroup;
        }

        return false;
      },

      hasRole: (role: string) => {
        const { roles } = get();
        return roles.includes(role);
      },

      hasAnyRole: (roles: string[]) => {
        const { roles: userRoles } = get();
        return roles.some((role) => userRoles.includes(role));
      },

      hasAllRoles: (roles: string[]) => {
        const { roles: userRoles } = get();
        return roles.every((role) => userRoles.includes(role));
      },

      isSectionVisible: (sectionKey: string) => {
        // Les admins ont accès à toutes les sections
        if (get().isAdmin()) return true;

        const { sections } = get();
        return sections[sectionKey]?.visible ?? false;
      },

      canList: (resourceKey: string) => {
        // Les admins peuvent lister toutes les ressources
        if (get().isAdmin()) return true;

        const { capabilities } = get();
        return capabilities[resourceKey]?.list ?? false;
      },

      canCreate: (resourceKey: string) => {
        // Les admins peuvent créer toutes les ressources
        if (get().isAdmin()) return true;

        const { capabilities } = get();
        return capabilities[resourceKey]?.create ?? false;
      },

      getCount: (key: string) => {
        const { counts } = get();
        return counts[key] ?? 0;
      },
    }),
    { name: 'AccessStore' }
  )
);

// Selectors optimisés pour éviter les re-renders inutiles
export const selectAuthenticated = (state: AccessState) => state.authenticated;
export const selectBootstrapLoaded = (state: AccessState) =>
  state.bootstrapLoaded;
export const selectUser = (state: AccessState) => state.user;
export const selectRoles = (state: AccessState) => state.roles;
export const selectSections = (state: AccessState) => state.sections;
export const selectCapabilities = (state: AccessState) => state.capabilities;
export const selectScope = (state: AccessState) => state.scope;
export const selectCounts = (state: AccessState) => state.counts;
export const selectPartial = (state: AccessState) => state.partial;
